import type { UPracticeData } from 'shared/definitions/progress-data';
import type { ClientUserStore } from 'shared/src';
import { sum } from 'shared/src/utils/sum';

let trackerCount = 1;

const learningSessionTrackers: { [lessonId: string]: LearningSessionTracker } = {};

export class LearningSessionTracker {
  private initialStats: ReturnType<typeof getStats>;

  private timeSpent = 0;

  private rightAnswers = 0;

  private wrongAnswers = 0;

  constructor(
    private clientUserStore: ClientUserStore,
    public id: string = `tracker-${trackerCount++}`
  ) {
    this.initialStats = getStats(clientUserStore);
  }

  public addTime(milliseconds: number) {
    this.timeSpent += milliseconds;
  }

  public addPracticeData(uPracticeData: UPracticeData[]) {
    this.timeSpent += uPracticeData.map((d) => d.totalTime || 0).reduce(sum, 0);
    this.rightAnswers += uPracticeData.map((d) => d.rightAnswers || 0).reduce(sum, 0);
    this.wrongAnswers += uPracticeData.map((d) => d.wrongAnswers || 0).reduce(sum, 0);
  }

  public end() {
    // Remove self from learningSessionTrackers
    delete learningSessionTrackers[this.id];
    // Calculate final stats:
    const finalStats = getStats(this.clientUserStore);
    const deltaStats = getDeltaStats(this.initialStats, finalStats);

    const totalQuestions = this.rightAnswers + this.wrongAnswers;
    // console.log({
    //   rightAnswers: this.rightAnswers,
    //   wrongAnswers: this.wrongAnswers,
    //   totalQuestions,
    // });
    const accuracy = totalQuestions ? (this.rightAnswers * 100) / totalQuestions : 0;
    return {
      ...deltaStats,
      accuracy,
      timeSpent: this.timeSpent,
    };
  }
}

type Stats = ReturnType<typeof getStats>;

function getDeltaStats(initialStats: Stats, finalStats: Stats) {
  const deltaTopics = finalStats.topics
    .map((finalTopic) => {
      const initialTopic = initialStats.topics.find((topic) => topic.id === finalTopic.id);
      const initialScore = initialTopic ? initialTopic.score : finalTopic.score;
      const finalScore = finalTopic.score;
      const initialNumQuestions = initialTopic
        ? initialTopic.numQuestions
        : finalTopic.numQuestions;
      const finalNumQuestions = finalTopic.numQuestions;
      return {
        id: finalTopic.id,
        initialScore,
        finalScore,
        deltaScore: finalScore - initialScore,
        initialNumQuestions,
        finalNumQuestions,
        deltaNumQuestions: finalNumQuestions - initialNumQuestions,
      };
    })
    .filter((topic) => topic.deltaNumQuestions !== 0);

  const deltaSkills = finalStats.skills
    .map((finalSkill) => {
      const initialSkill = initialStats.skills.find((skill) => skill.id === finalSkill.id);
      const initialScore = initialSkill ? initialSkill.score : finalSkill.score;
      const finalScore = finalSkill.score;
      const initialNumQuestions = initialSkill
        ? initialSkill.numQuestions
        : finalSkill.numQuestions;
      const finalNumQuestions = finalSkill.numQuestions;
      return {
        id: finalSkill.id,
        initialScore,
        finalScore,
        deltaScore: finalScore - initialScore,
        initialNumQuestions,
        finalNumQuestions,
        deltaNumQuestions: finalNumQuestions - initialNumQuestions,
      };
    })
    .filter((skill) => skill.deltaNumQuestions !== 0);
  const deltaLifetimeAccuracy = finalStats.lifetimeAccuracy - initialStats.lifetimeAccuracy;
  const deltaUPoints = finalStats.uPoints - initialStats.uPoints;

  const questionsAnswered = {
    initial: initialStats.questionsAnswered,
    final: finalStats.questionsAnswered,
    delta: finalStats.questionsAnswered - initialStats.questionsAnswered,
  };

  return {
    topics: deltaTopics,
    skills: deltaSkills,
    lifetimeAccuracy: {
      initialLifteimeAccuracy: initialStats.lifetimeAccuracy,
      finalLifteimeAccuracy: initialStats.lifetimeAccuracy,
      deltaLifetimeAccuracy,
    },
    uPoints: {
      initial: initialStats.uPoints,
      final: finalStats.uPoints,
      delta: deltaUPoints,
    },
    questionsAnswered,
  };
}

function getStats(clientUserStore: ClientUserStore) {
  // Grab initial state of skills and lessons
  const skills = clientUserStore.knowledgeProgress.skills.map((skill) => ({
    id: skill.textId,
    score: skill.score,
    numQuestions: skill.numQuestions,
  }));

  const topics = clientUserStore.knowledgeProgress.topics.map((topic) => ({
    id: topic.textId,
    score: topic.score,
    numQuestions: topic.numQuestions,
  }));

  const lessonAreas = clientUserStore.lessonProgress.areas.map((area) => ({
    id: area.textId,
    score: area.progress,
  }));

  const lifetimeAccuracy = clientUserStore.knowledgeProgress.accuracyScore;

  const uPoints = clientUserStore.uPoints;

  const questionsAnswered = clientUserStore.knowledgeProgress.numQuestions;

  return {
    skills,
    topics,
    lessonAreas,
    lifetimeAccuracy,
    uPoints,
    questionsAnswered,
  };
}

export function getLearningSessionTracker(clientUserStore: ClientUserStore, id?: string) {
  if (id && learningSessionTrackers[id]) return learningSessionTrackers[id]!;
  const learningTracker = new LearningSessionTracker(clientUserStore, id);
  learningSessionTrackers[learningTracker.id] = learningTracker;
  return learningTracker;
}
